<template>
    <div class="file-card-list" :class="{row: !autoWidth && !list, 'file-card-wrapper-auto': autoWidth && !list, listed: list}" role="list">
        <template v-if="files.length">
            <div v-for="file in files" :class="colClasses" role="listitem">
                <file-list-card :file="file" @delete-file="deleteFile" :deletable="deletable" :delete-path="deletePath" :delete-name="deleteName" :show-file-size="showFileSize"
                    :show-created="showCreated" :lightboxId="lightboxId" :description-key="descriptionKey" :force-download-link="forceDownloadLink" :key="'file-list-' + file.id">
                    <template v-if="!SubSlotIsEmpty" #sub="properties">
                        <slot name="file-card-sub" v-bind="properties"></slot>
                    </template>
                </file-list-card>
            </div>
        </template>

        <slot name="additional"/>
    </div>
</template>

<script setup lang="ts">
    import useEmptySlots from '@/composables/emptySlotCheck';
    import {usePortuna} from '@/composables/portuna';
    import {uniqueId} from '@/library/string';
    import TopMessageHandler from '../library/topMessageHandler';
    import {sendPopupRequest} from '@/utils/portuna';
    import FileListCard from './FileListCard.vue';
    import {computed, toRefs} from 'vue';

    const portuna = usePortuna();

    const emit = defineEmits<{
        (eventName: 'deleted', file: FileOrImageUpload): void;
        (eventName: 'delete', files: FileOrImageUpload[]): void;
        (eventName: 'update:files', files: FileOrImageUpload[]): void;
    }>();

    const props = withDefaults(
        defineProps<{
            files: FileOrImageUpload[];
            descriptionKey?: keyof FileOrImageUpload;
            showCreated?: boolean;
            deletable?: boolean;
            preventMutation?: boolean;
            cols?: number;
            colPrefix?: string;
            deletePath?: string | boolean;
            showFileSize?: boolean;
            autoWidth?: boolean;
            deleteName?: string;
            forceDownloadLink?: boolean;
            list?: boolean;
        }>(),
        {
            descriptionKey: 'description',
            showCreated: true,
            deletable: false,
            preventMutation: false,
            cols: 6,
            colPrefix: '',
            deletePath: '',
            showFileSize: true,
            autoWidth: false,
            deleteName: 'delete_files',
            forceDownloadLink: true,
            list: false,
        },
    );

    const {
              descriptionKey,
              showCreated,
              deletable,
              preventMutation,
              cols,
              colPrefix,
              deletePath,
              showFileSize,
              autoWidth,
              deleteName,
              forceDownloadLink,
              list,
          } = toRefs(props);

    const files = computed({
        get: () => props.files,
        set: (files) => emit('update:files', files),
    });

    const slots = defineSlots<{
        ['file-card-sub'](): void,
        additional(): void,
    }>();

    const {targetSlotIsEmpty: SubSlotIsEmpty} = useEmptySlots('file-card-sub', {
        'file-card-sub': {
            file: {},
            size: '',
        },
    });

    const lightboxId = uniqueId('file-card-lightbox-');

    const colClasses = computed(() =>
    {
        let classes: string[] = [];
        if (autoWidth.value)
        {
            classes.push('text-overflow');

            return classes;
        }

        if (!list.value)
        {
            classes.push(colPrefix.value + 'col-md-12');

            classes.push(colPrefix.value + 'col-lg-' + cols.value);
        }

        return classes;
    });

    const deleteFile = (file: FileOrImageUpload): void =>
    {
        if (deletePath.value === true)
        {
            emit('deleted', file);
        }
        else if (typeof deletePath.value === 'string')
        {
            let deleteUrl = deletePath.value
            .replace('{id}', file.id.toString())
            .replace('{assignment_id}', file.assignment_id.toString());

            sendPopupRequest({
                url: deleteUrl,
                type: 'deleteFile',
                message: {
                    headline: 'Datei löschen',
                    message:
                        'Sind Sie sicher, dass Sie die Datei "<strong>' +
                        file.filename +
                        '<' +
                        '/strong>" wirklich löschen möchten?',
                    success: 'Die Datei "' + file.filename + '" wurde erfolgreich gelöscht.',
                    failed: 'Die Datei konnte nicht gelöscht werden.',
                },
                options: {
                    textIcon: 'delete_forever',
                    textIconClass: 'text-danger',
                    titleIcon: 'delete',
                    buttonOkay: {
                        type: 'danger',
                        icon: 'delete',
                        title: 'Löschen',
                    },
                },
            })
            .then(() =>
            {
                let _files = JSON.parse(JSON.stringify(files.value));
                for (let i in _files)
                {
                    if (_files.hasOwnProperty(i) && _files[i].id === file.id)
                    {
                        _files.splice(i, 1);

                        break;
                    }
                }

                emit('delete', _files);

                if (!preventMutation.value)
                {
                    files.value = _files;
                }
            })
            .catch((e) =>
            {
                e !== 'popupClosed' &&
                TopMessageHandler.error(
                    'Beim Löschen der Datei ' + file.filename + ' ist ein Fehler aufgetreten.',
                );
            });
        }
    };

    defineExpose(Object.assign({}, portuna, {componentName: 'FileList'}));
</script>